import { useQuery, useMutation, UseQueryOptions, UseMutationOptions } from '@tanstack/react-query';
import { environment } from '../config/environment';
import { AuthManager } from '../utils/auth';

// Base fetch wrapper with auth and error handling
async function apiFetch<T = any>(
  endpoint: string,
  options?: RequestInit & { skipAuth?: boolean }
): Promise<T> {
  const url = `${environment.api.baseUrl}${endpoint}`;
  
  // For public endpoints (like trial subscription signup), skip auth checks
  const isPublicEndpoint = options?.skipAuth || endpoint.includes('/subscription/trial') || endpoint.includes('/plans');
  
  // Check if token is expired before making request (skip for public endpoints)
  if (!isPublicEndpoint && AuthManager.isTokenExpired()) {
    AuthManager.handleAuthError();
    throw new Error('Authentication expired');
  }
  
  // Get auth token from storage (only for authenticated endpoints)
  const token = !isPublicEndpoint ? AuthManager.getToken() : null;
  
  const response = await fetch(url, {
    ...options,
    headers: {
      // Only add default Content-Type if not explicitly set to null
      ...(options?.headers && !Object.hasOwnProperty.call(options.headers, 'Content-Type') && { 'Content-Type': 'application/json' }),
      ...(token && { Authorization: `Bearer ${token}` }),
      // Spread options.headers last, but filter out null Content-Type
      ...(options?.headers && Object.fromEntries(
        Object.entries(options.headers).filter(([key, value]) => !(key === 'Content-Type' && value === null))
      )),
    },
  });

  if (!response.ok) {
    let errorData: any = {};
    let errorMessage = response.statusText;
    
    try {
      errorData = await response.json();
      errorMessage = errorData.message || errorData.error || response.statusText;
    } catch (parseError) {
      // If response is not JSON, use statusText
      console.error('Failed to parse error response:', parseError);
    }
    
    // Handle specific error cases
    if (response.status === 401) {
      // Use AuthManager to handle auth error
      AuthManager.handleAuthError();
      throw new Error('Authentication expired. Please login again.');
    }
    
    if (response.status === 403) {
      errorMessage = 'You do not have permission to access this resource.';
    }
    
    const apiError = new Error(errorMessage);
    (apiError as any).status = response.status;
    (apiError as any).data = errorData;
    (apiError as any).endpoint = endpoint;
    
    // Log error details for debugging
    console.error(`API Error [${response.status}] ${endpoint}:`, {
      status: response.status,
      statusText: response.statusText,
      errorData,
      headers: Object.fromEntries(response.headers.entries())
    });
    
    throw apiError;
  }

  // Handle empty responses (204, 205)
  if (response.status === 204 || response.status === 205) {
    return null as T;
  }

  return response.json();
}

// Specialized fetch for blob responses (file downloads)
async function apiFetchBlob(
  endpoint: string,
  options?: RequestInit & { skipAuth?: boolean }
): Promise<Blob> {
  const url = `${environment.api.baseUrl}${endpoint}`;
  
  // For public endpoints, skip auth checks
  const isPublicEndpoint = options?.skipAuth || endpoint.includes('/subscription/trial') || endpoint.includes('/plans');
  
  // Check if token is expired before making request (skip for public endpoints)
  if (!isPublicEndpoint && AuthManager.isTokenExpired()) {
    AuthManager.handleAuthError();
    throw new Error('Authentication expired');
  }
  
  // Get auth token from storage (only for authenticated endpoints)
  const token = !isPublicEndpoint ? AuthManager.getToken() : null;
  
  const response = await fetch(url, {
    ...options,
    headers: {
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options?.headers,
    },
  });

  if (!response.ok) {
    let errorMessage = response.statusText;
    
    // For blob endpoints, we might get JSON error responses
    try {
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const errorData = await response.json();
        errorMessage = errorData.message || errorData.error || response.statusText;
      }
    } catch (parseError) {
      // If response is not JSON, use statusText
      console.error('Failed to parse error response:', parseError);
    }
    
    // Handle specific error cases
    if (response.status === 401) {
      AuthManager.handleAuthError();
      throw new Error('Authentication expired. Please login again.');
    }
    
    if (response.status === 403) {
      errorMessage = 'You do not have permission to access this resource.';
    }
    
    const apiError = new Error(errorMessage);
    (apiError as any).status = response.status;
    (apiError as any).endpoint = endpoint;
    
    console.error(`API Error [${response.status}] ${endpoint}:`, {
      status: response.status,
      statusText: response.statusText,
      headers: Object.fromEntries(response.headers.entries())
    });
    
    throw apiError;
  }

  return response.blob();
}

// Custom hook for GET requests
export function useApiQuery<TData = any, TError = any>(
  queryKey: string[],
  endpoint: string,
  options?: Omit<UseQueryOptions<TData, TError>, 'queryKey' | 'queryFn'>
) {
  return useQuery<TData, TError>({
    queryKey,
    queryFn: () => apiFetch<TData>(endpoint),
    ...options,
  });
}

// Custom hook for mutations (POST, PUT, DELETE)
export function useApiMutation<TData = any, TError = any, TVariables = any>(
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  endpoint: string | ((variables: TVariables) => string),
  options?: UseMutationOptions<TData, TError, TVariables> & { requestType?: 'json' | 'multipart'; skipAuth?: boolean }
) {
  return useMutation<TData, TError, TVariables>({
    mutationFn: async (variables) => {
      const url = typeof endpoint === 'function' ? endpoint(variables) : endpoint;
      const isMultipart = options?.requestType === 'multipart';
      
      let body: string | FormData | undefined;
      let headers: Record<string, string> = {};
      
      if (variables && method !== 'DELETE' && method !== 'GET') {
        if (isMultipart) {
          // For multipart requests, create FormData
          const formData = new FormData();
          Object.entries(variables as any).forEach(([key, value]) => {
            if (value instanceof File) {
              formData.append(key, value);
            } else if (value !== undefined && value !== null) {
              formData.append(key, String(value));
            }
          });
          body = formData;
          // Set Content-Type to null to prevent default application/json
          headers['Content-Type'] = null as any;
        } else {
          // For JSON requests
          body = JSON.stringify(variables);
          headers['Content-Type'] = 'application/json';
        }
      }
      
      return apiFetch<TData>(url, {
        method,
        body,
        headers,
        skipAuth: options?.skipAuth,
      });
    },
    ...options,
  });
}

// Convenience hooks for specific HTTP methods
export const useApiPost = <TData = any, TError = any, TVariables = any>(
  endpoint: string,
  options?: UseMutationOptions<TData, TError, TVariables> & { skipAuth?: boolean }
) => useApiMutation<TData, TError, TVariables>('POST', endpoint, options);

export const useApiPut = <TData = any, TError = any, TVariables = any>(
  endpoint: string | ((variables: TVariables) => string),
  options?: UseMutationOptions<TData, TError, TVariables> & { skipAuth?: boolean }
) => useApiMutation<TData, TError, TVariables>('PUT', endpoint, options);

export const useApiDelete = <TData = any, TError = any, TVariables = any>(
  endpoint: string | ((variables: TVariables) => string),
  options?: UseMutationOptions<TData, TError, TVariables> & { skipAuth?: boolean }
) => useApiMutation<TData, TError, TVariables>('DELETE', endpoint, options);

// Specialized hook for blob downloads (like CSV exports)
export function useApiBlobMutation<TError = any, TVariables = any>(
  method: 'GET' | 'POST',
  endpoint: string | ((variables: TVariables) => string),
  options?: UseMutationOptions<Blob, TError, TVariables>
) {
  return useMutation<Blob, TError, TVariables>({
    mutationFn: async (variables) => {
      const url = typeof endpoint === 'function' ? endpoint(variables) : endpoint;
      
      return apiFetchBlob(url, {
        method,
        ...(variables && method !== 'GET' && {
          body: JSON.stringify(variables),
        }),
      });
    },
    ...options,
  });
}